<script setup lang="ts">
/**
 * Les alarmes d'une surveillance, sur sa fiche (« Vie de la surveillance »,
 * S:5548-5561, lue par la route E filtrée sur la surveillance).
 *
 * Ce que l'utilisateur doit vivre : il lit ce qui est arrivé à la
 * surveillance, la plus récente d'abord, et ouvre une alarme par un lien. La
 * liste ne lit jamais le détail (le lire clôt une alarme revenue à la normale
 * et non vue, D71) : c'est le lien, cliqué par la personne, qui l'ouvre. Une
 * liste arrêtée au plafond dit qu'elle n'est pas complète.
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"
import { etatDeLaLigne, type EtatLigne } from "@modules/alarmes-v2/utils/etats"
import { formaterInstant } from "@modules/alarmes-v2/utils/mesure"
import type { StatutHistorique } from "@modules/alarmes-v2/stores/fiche.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// TYPES & INTERFACES
// ================================
/** Une ligne de l'historique. */
interface Ligne {
    alarme: AlarmesV2Alarme
    quand: string
    etat: EtatLigne
    lien: string
    libelleLien: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La surveillance : son état sert aux alarmes terminées. */
        surveillance: AlarmesV2Surveillance
        /** Ses alarmes, de la plus ancienne à la plus récente. */
        alarmes: AlarmesV2Alarme[]
        /** Le chemin de la fiche : le détail est sous `alarme/<id>`. */
        cheminFiche: string
        statut?: StatutHistorique
        incomplet?: boolean
        personnes?: Record<string, AlarmesV2Personne>
    }>(),
    { statut: "pret", incomplet: false, personnes: () => ({}) }
)

// ================================
// COMPUTED
// ================================
/** L'état d'une alarme terminée : « Close », avec son heure. */
function etatClose(a: AlarmesV2Alarme): EtatLigne {
    return {
        regle: 0,
        code: "armee",
        icone: "○",
        ton: "neutre",
        mot: t("alarmesV2.fiche.close"),
        detail: formaterInstant(a.close_le, tr) ?? "",
        second: "",
    }
}

/** Les lignes, la plus récente d'abord. */
const getLignes = computed((): Ligne[] =>
    [...props.alarmes].reverse().map((a) => {
        const info =
            a.nature === "pour_information" ||
            a.etat === "declenchee_pour_information"
        return {
            alarme: a,
            quand: t("alarmesV2.fiche.ouverteLe", {
                quand:
                    formaterInstant(a.ouverte_le, tr) ??
                    t("alarmesV2.mesure.absente"),
            }),
            etat:
                a.etat === "close" || a.ouverte === false
                    ? etatClose(a)
                    : etatDeLaLigne(props.surveillance, a, tr, props.personnes),
            lien: `${props.cheminFiche}/alarme/${encodeURIComponent(a._id)}`,
            libelleLien: info
                ? t("alarmesV2.fiche.voirLInformation")
                : t("alarmesV2.fiche.voirLAlarme"),
        }
    })
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section class="flex flex-col gap-2" data-test="historique">
        <h2 class="text-sm font-semibold text-surface-900">
            {{ t("alarmesV2.fiche.historique") }}
        </h2>
        <p
            v-if="statut === 'erreur'"
            class="text-sm text-surface-700"
            data-test="historique-erreur"
        >
            {{ t("alarmesV2.fiche.historiqueIndisponible") }}
        </p>
        <p v-else-if="statut !== 'pret'" class="text-sm text-surface-600">
            {{ t("alarmesV2.fiche.chargement") }}
        </p>
        <p
            v-else-if="getLignes.length === 0 && !incomplet"
            class="text-sm text-surface-600"
            data-test="historique-vide"
        >
            {{ t("alarmesV2.fiche.historiqueVide") }}
        </p>
        <ul v-else class="flex flex-col divide-y divide-surface-200">
            <li
                v-for="l in getLignes"
                :key="l.alarme._id"
                class="flex flex-wrap items-center gap-3 py-2 text-sm"
                data-test="ligne-historique"
            >
                <span class="w-44 text-surface-700">{{ l.quand }}</span>
                <EtatSurveillance :etat="l.etat" />
                <span class="text-surface-700">{{ l.etat.detail }}</span>
                <RouterLink
                    :to="l.lien"
                    class="ml-auto font-medium text-primary-700 underline"
                    data-test="lien-alarme"
                    >{{ l.libelleLien }}</RouterLink
                >
            </li>
        </ul>
        <p
            v-if="statut === 'pret' && incomplet"
            class="text-xs text-surface-600"
            data-test="historique-incomplet"
        >
            {{ t("alarmesV2.fiche.historiqueIncomplet") }}
        </p>
    </section>
</template>
